// File: CashOffersPanel.js (FULL DROP-IN)
// ✅ Rules-aligned: reads only offers where sellerUid == current user
// ✅ PII-free: no email display
// ✅ No client-side accept/decline (rules forbid it)
// ✅ Uses PublicTrades for titles (public), falls back safely
// ✅ FIXES (kept):
//    - Avoids "missing index / failed-precondition" crashes by falling back when orderBy query fails
//    - Avoids "missing createdAt" crashes by doing a safe fallback query and sorting client-side
//    - More schema-tolerant tradeId detection (tradeId / requestedItemId / meta.tradeId / trade.id)
//    - Caches hydrated titles to reduce reads
//    - Removes duplicate <h3> because OwnerDashboard already renders the section heading
//    - Optional live updates via onSnapshot (more "dashboard-like")
// ✅ NEW UI features:
//    - Filter pills (All / Pending / Accepted)
//    - Sorting (Newest / Highest offer)
//    - "Open trade" button linking to /trade/:id (safe, no contact)
//    - Premium compact card layout + status badge + skeleton loading

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import "./CashOffersPanel.css";

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const safeStr = (v) => String(v ?? "").trim();
const lower = (v) => safeStr(v).toLowerCase();

const toMs = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    if (typeof ts?.seconds === "number") return ts.seconds * 1000;
    if (typeof ts === "number") return ts;
    return 0;
  } catch {
    return 0;
  }
};

const pickTradeId = (o) =>
  safeStr(
    o?.tradeId ||
      o?.requestedItemId ||
      o?.meta?.tradeId ||
      o?.trade?.id ||
      o?.tradeRefId ||
      ""
  );

const shortUid = (uid) => {
  const s = safeStr(uid);
  if (!s) return "Unknown";
  return s.length <= 10 ? s : `${s.slice(0, 8)}…`;
};

const statusLabel = (s) => {
  const k = lower(s || "pending");
  if (k === "accepted") return "Accepted";
  if (k === "declined" || k === "rejected") return "Declined";
  if (k === "cancelled" || k === "canceled") return "Cancelled";
  if (k === "expired") return "Expired";
  return "Pending";
};

const statusTone = (s) => {
  const k = lower(s || "pending");
  if (k === "accepted") return "ok";
  if (k === "declined" || k === "rejected") return "bad";
  if (k === "cancelled" || k === "canceled" || k === "expired") return "muted";
  return "warn";
};

const CashOffersPanel = () => {
  const { currentUser } = useAuth();

  const [cashOffers, setCashOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // NEW: UI controls
  const [filter, setFilter] = useState("all"); // all | pending | accepted
  const [sortBy, setSortBy] = useState("newest"); // newest | highest

  // Cache hydrated titles to reduce reads
  const titleCacheRef = useRef(new Map()); // tradeId -> title

  const hydrateTradeTitle = useCallback(async (tradeId) => {
    const tid = safeStr(tradeId);
    if (!tid) return { tradeTitle: "Unknown item" };

    const cached = titleCacheRef.current.get(tid);
    if (cached) return { tradeTitle: cached };

    // Prefer PublicTrades (always readable)
    try {
      const pubSnap = await getDoc(doc(db, "PublicTrades", tid));
      if (pubSnap.exists()) {
        const d = pubSnap.data() || {};
        const title = safeStr(d.title) || "Untitled item";
        titleCacheRef.current.set(tid, title);
        return { tradeTitle: title };
      }
    } catch {
      // ignore
    }

    // Best-effort fallback to Trades (may be blocked by rules)
    try {
      const canonSnap = await getDoc(doc(db, "Trades", tid));
      if (canonSnap.exists()) {
        const d = canonSnap.data() || {};
        const title = safeStr(d.title) || "Untitled item";
        titleCacheRef.current.set(tid, title);
        return { tradeTitle: title };
      }
    } catch {
      // ignore
    }

    titleCacheRef.current.set(tid, "Unknown item");
    return { tradeTitle: "Unknown item" };
  }, []);

  const enrichOffers = useCallback(
    async (raw) => {
      // Keep only cash offers (schema tolerant)
      const cashOnly = raw.filter((o) => lower(o.offerType) === "cash" || lower(o.type) === "cash");

      // Hydrate titles + resolve tradeId once
      const withTitles = await Promise.all(
        cashOnly.map(async (o) => {
          const tid = pickTradeId(o);
          const t = await hydrateTradeTitle(tid);
          return { ...o, ...t, _tradeIdResolved: tid };
        })
      );

      // Default sort newest first (safe when createdAt missing)
      withTitles.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));

      return withTitles;
    },
    [hydrateTradeTitle]
  );

  const subscribeOrFetch = useCallback(() => {
    if (!currentUser?.uid) {
      setCashOffers([]);
      setLoading(false);
      return () => {};
    }

    setLoading(true);
    setErr("");

    const offersCol = collection(db, "TradeOffers");

    // Primary: indexed query (may require composite index depending on your project)
    const qPrimary = query(
      offersCol,
      where("sellerUid", "==", currentUser.uid),
      where("offerType", "==", "cash"),
      orderBy("createdAt", "desc"),
      limit(60)
    );

    // Fallback: rule-safe but index-light; filter/sort client-side
    const qFallback = query(offersCol, where("sellerUid", "==", currentUser.uid), limit(200));

    let unsub = () => {};

    const startFallbackListener = () => {
      try {
        unsub = onSnapshot(
          qFallback,
          async (snap) => {
            try {
              const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
              const enriched = await enrichOffers(raw);
              setCashOffers(enriched.slice(0, 60));
              setErr("");
            } catch (e) {
              console.error("[CashOffersPanel] fallback enrich error:", e);
              setErr("Failed to load cash offers.");
              setCashOffers([]);
            } finally {
              setLoading(false);
            }
          },
          (e) => {
            console.error("[CashOffersPanel] fallback listener error:", e);
            setErr("Failed to load cash offers.");
            setCashOffers([]);
            setLoading(false);
          }
        );
      } catch (e) {
        console.error("[CashOffersPanel] fallback start error:", e);
        setErr("Failed to load cash offers.");
        setCashOffers([]);
        setLoading(false);
      }
    };

    try {
      unsub = onSnapshot(
        qPrimary,
        async (snap) => {
          try {
            const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const enriched = await enrichOffers(raw);
            setCashOffers(enriched);
            setErr("");
          } catch (e) {
            console.error("[CashOffersPanel] enrich error:", e);
            setErr("Failed to load cash offers.");
            setCashOffers([]);
          } finally {
            setLoading(false);
          }
        },
        async (e) => {
          console.warn("[CashOffersPanel] primary listener error, falling back:", e);
          startFallbackListener();
        }
      );
    } catch (e) {
      console.warn("[CashOffersPanel] primary start error, falling back:", e);
      startFallbackListener();
    }

    return () => {
      try {
        unsub();
      } catch {}
    };
  }, [currentUser?.uid, enrichOffers]);

  useEffect(() => {
    const unsub = subscribeOrFetch();
    return () => unsub?.();
  }, [subscribeOrFetch]);

  // Last resort one-time fetch (kept)
  const oneTimeFetchFallback = useCallback(async () => {
    if (!currentUser?.uid) return;
    setLoading(true);
    setErr("");
    try {
      const offersCol = collection(db, "TradeOffers");

      // Try primary getDocs first
      try {
        const snap = await getDocs(
          query(
            offersCol,
            where("sellerUid", "==", currentUser.uid),
            where("offerType", "==", "cash"),
            orderBy("createdAt", "desc"),
            limit(60)
          )
        );
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const enriched = await enrichOffers(raw);
        setCashOffers(enriched);
        setLoading(false);
        return;
      } catch (e) {
        // ignore, fall through
      }

      // Light query then filter
      const snap2 = await getDocs(
        query(offersCol, where("sellerUid", "==", currentUser.uid), limit(200))
      );
      const raw2 = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
      const enriched2 = await enrichOffers(raw2);
      setCashOffers(enriched2.slice(0, 60));
    } catch (e) {
      console.error("[CashOffersPanel] oneTimeFetchFallback error:", e);
      setErr("Failed to load cash offers.");
      setCashOffers([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, enrichOffers]);

  useEffect(() => {
    if (!loading && err) {
      oneTimeFetchFallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [err]);

  // --- Derived: totals + filtered + sorted ---
  const totals = useMemo(() => {
    const total = cashOffers.length;
    const pending = cashOffers.filter((o) => lower(o.status) === "pending" || !safeStr(o.status)).length;
    const accepted = cashOffers.filter((o) => lower(o.status) === "accepted").length;
    return { total, pending, accepted };
  }, [cashOffers]);

  const filteredSorted = useMemo(() => {
    const list = Array.isArray(cashOffers) ? [...cashOffers] : [];

    // Filter
    const filtered =
      filter === "pending"
        ? list.filter((o) => lower(o.status || "pending") === "pending" || !safeStr(o.status))
        : filter === "accepted"
        ? list.filter((o) => lower(o.status) === "accepted")
        : list;

    // Sort
    if (sortBy === "highest") {
      filtered.sort((a, b) => {
        const av = Number(a?.proposedCashAmount ?? a?.cashAmount ?? 0) || 0;
        const bv = Number(b?.proposedCashAmount ?? b?.cashAmount ?? 0) || 0;
        if (bv !== av) return bv - av;
        return toMs(b.createdAt) - toMs(a.createdAt);
      });
    } else {
      // newest
      filtered.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
    }

    return filtered;
  }, [cashOffers, filter, sortBy]);

  if (!currentUser) return null;

  return (
    <div className="cash-panel">
      {/* Header row (OwnerDashboard already has section title, so keep this compact) */}
      <div className="cash-panel-head">
        <div className="cash-panel-title">
          <div className="cash-panel-sub">
            {loading
              ? "Loading…"
              : `${totals.total} total · ${totals.pending} pending · ${totals.accepted} accepted`}
          </div>
        </div>

        <div className="cash-panel-controls">
          {/* Filter pills */}
          <div className="cash-filters" role="tablist" aria-label="Cash offer filter">
            <button
              type="button"
              className={`cash-pill ${filter === "all" ? "active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={`cash-pill ${filter === "pending" ? "active" : ""}`}
              onClick={() => setFilter("pending")}
            >
              Pending
            </button>
            <button
              type="button"
              className={`cash-pill ${filter === "accepted" ? "active" : ""}`}
              onClick={() => setFilter("accepted")}
            >
              Accepted
            </button>
          </div>

          {/* Sort */}
          <select
            className="cash-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort cash offers"
          >
            <option value="newest">Newest</option>
            <option value="highest">Highest offer</option>
          </select>

          {/* Refresh */}
          <button className="cash-panel-refresh" type="button" onClick={oneTimeFetchFallback} disabled={loading}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {err ? <div className="cash-panel-error">{err}</div> : null}

      {loading ? (
        <div className="cash-skeleton-list">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="cash-skeleton" />
          ))}
        </div>
      ) : !err && filteredSorted.length === 0 ? (
        <div className="cash-empty">
          <div className="cash-empty-title">No offers match this filter</div>
          <div className="cash-empty-sub">Try switching to “All” to view the full list.</div>
        </div>
      ) : (
        <div className="cash-grid">
          {filteredSorted.map((offer) => {
            const status = safeStr(offer.status || "pending");
            const buyerUid = safeStr(offer.buyerUid || offer.fromUid);
            const tid = pickTradeId(offer) || safeStr(offer._tradeIdResolved);

            const amount =
              offer.proposedCashAmount != null
                ? moneyZAR(offer.proposedCashAmount)
                : "—";

            const msg = safeStr(offer.message) ? safeStr(offer.message) : "—";

            return (
              <article key={offer.id} className="cash-card">
                <div className="cash-card-top">
                  <div className="cash-trade">
                    <div className="cash-trade-label">Trade item</div>
                    <div className="cash-trade-title">{offer.tradeTitle || "Untitled item"}</div>
                  </div>

                  <div className={`cash-badge ${statusTone(status)}`}>
                    {statusLabel(status)}
                  </div>
                </div>

                <div className="cash-amount-strip">
                  <span>Proposed cash offer</span>
                  <strong>{amount}</strong>
                </div>

                <div className="cash-meta cash-meta--compact">
                  <div className="cash-meta-row">
                    <span className="k">Buyer reference</span>
                    <span className="v">{shortUid(buyerUid)}</span>
                  </div>
                </div>

                {msg && msg !== "—" ? (
                  <div className="cash-msg">
                    <div className="cash-msg-label">Buyer message</div>
                    <div className="cash-msg-text">{msg}</div>
                  </div>
                ) : (
                  <div className="cash-msg cash-msg--empty">
                    No buyer message included.
                  </div>
                )}

                <div className="cash-card-actions">
                  {tid ? (
                    <Link to={`/trade/${tid}`} className="cash-open-btn">
                      Open trade
                    </Link>
                  ) : (
                    <span className="cash-open-disabled">Trade link unavailable</span>
                  )}

                  <span className={`cash-action-note ${lower(status) === "pending" ? "is-pending" : ""}`}>
                    {lower(status) === "pending" ? "Awaiting secure processing" : "Processed securely"}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CashOffersPanel;
